<template>
  <div v-if="site">
    <c-row class="mb-3">
      <c-col sm="12" md="4">
        <c-card>
          <c-card-body>
            <c-row>
              <c-col sm="10">
                <h1 class="h4" v-html="site.name" />
                <c-button
                  v-if="wpUpdateVersionFound"
                  color="kb-green"
                  :disabled="isUpdating"
                  @click="openConfirmUpdateVersionModal"
                >
                  <span v-if="isUpdating">
                    Updating WordPress
                    <c-spinner color="black" size="sm" />
                  </span>
                  <span v-else>Update WordPress to {{ wpUpdateVersionFound }}</span>
                </c-button>
              </c-col>
              <c-col sm="2">
                <c-dropdown
                  color="primary"
                  size="sm"
                  toggler-text="Small"
                  class="d-inline-block float-right"
                  :caret="false"
                >
                  <template #toggler-content>
                    <c-icon :content="freeSet.cilSettings" />
                    <span class="sr-only">Search</span>
                  </template>
                  <c-dropdown-item v-show="$auth.check('organisation-admin')" @click="openConfirmDelete">
                    Remove site from KoalaBoss
                  </c-dropdown-item>
                  <c-dropdown-item @click="showEdit = true">Edit site</c-dropdown-item>
                  <c-dropdown-item @click="refreshSite({ takeScreenshot: true })">
                    <c-spinner v-if="isRefreshingSite" color="success" size="sm" />
                    <span v-else>Refresh</span>
                  </c-dropdown-item>
                </c-dropdown>
              </c-col>
            </c-row>
            <hr />
            <template v-if="!site.screenshots">
              <initial-screenshot :url="site.siteAddress" />
              <!--                          <img v-if="site.initialScreenshotUrl" :alt="`screenshot 1 for ${site.name}`" :src="site.initialScreenshotUrl" class="w-100"/>-->
            </template>
            <template v-else>
              <img
                v-if="getScreenshotLarge('desktop')"
                :alt="`screenshot for ${site.name}`"
                :src="getScreenshotLarge('desktop')"
                class="w-100"
              />
            </template>
            <c-row class="mt-4">
              <c-col>
                <a :href="site.siteAddress" target="_blank">{{ site.siteAddress }}</a>
              </c-col>
            </c-row>
            <hr />
            <c-row class="mt-2">
              <c-col>
                <template v-if="client">
                  Client:
                  <router-link :to="{ name: 'clientDetails', params: { clientId: client._id } }">
                    <span v-html="client.name" />
                  </router-link>
                  <c-button color="primary" size="sm" class="float-right" @click="showEditClient = true">
                    Change client
                  </c-button>
                </template>
                <template v-else>
                  <a href="#" @click="showEditClient = true">Set client</a>
                </template>
              </c-col>
            </c-row>
            <hr />
            <c-row class="mt-2">
              <c-col>
                <div class="mb-2">
                  <strong>Site Details</strong>
                </div>
                <div class="hosting-data">
                  <span>Created</span>
                  <span>{{ site.createdAt | dateFormatRelative }}</span>
                </div>
                <div class="hosting-data">
                  <span>Updated</span>
                  <span>{{ site.updatedAt | dateFormatRelative }}</span>
                </div>
              </c-col>
            </c-row>
            <hr />
            <c-row class="mt-2">
              <c-col>
                <div class="mb-2">
                  <strong>Hosting Tech Details</strong>
                </div>
                <div class="hosting-data" v-if="parsedSiteData.wpVersion">
                  <span>WordPress Version</span>
                  <span>{{ parsedSiteData.wpVersion }}</span>
                </div>
                <div class="hosting-data" v-if="parsedSiteData.mysqlVersion">
                  <span>MySQL Version</span>
                  <span>{{ parsedSiteData.mysqlVersion }}</span>
                </div>
                <div class="hosting-data" v-if="parsedSiteData.phpVersion">
                  <span>PHP Version</span>
                  <span>{{ parsedSiteData.phpVersion }}</span>
                </div>
              </c-col>
            </c-row>
          </c-card-body>
        </c-card>
        <c-card>
          <c-card-header>Site Notes</c-card-header>
          <c-card-body>
            <div class="notes">
              <template v-if="!isEditingNotes">
                <div class="read-only" @click="editNotes">
                  <span v-if="data.body.notes" v-html="data.body.notesDisplay" />
                  <span v-else class="add-notes">Click to add notes</span>
                </div>
              </template>
              <template v-if="isEditingNotes">
                <c-textarea v-model="data.body.notes" placeholder="Add your notes" rows="9" autofocus />
                <c-button-group class="" size="sm">
                  <c-button class="btn btn-primary" :class="{ 'is-loading': isSavingNotes }" @click="saveNotes()">
                    Update
                  </c-button>
                  <c-button class="btn btn-secondary" @click="cancelEditNotes()">
                    Cancel
                  </c-button>
                </c-button-group>
              </template>
            </div>
          </c-card-body>
        </c-card>
      </c-col>
      <c-col sm="12" md="8">
        <c-tabs class="tab-set-1">
          <c-tab title="Plugins" active>
            <template #title>
              <plugin-icon />
              Plugins
              <pill :value="pluginUpdateCount" />
            </template>

            <template v-if="hasPluginData && site.scrapeData.pluginData.length">
              <template>
                <c-row class="p-1">
                  <c-col>
                    <p v-if="pluginUpdateCount">
                      <strong>
                        {{ pluginUpdateCount }} plugin{{ pluginUpdateCount > 1 ? 's' : '' }} need{{
                          pluginUpdateCount === 1 ? 's' : ''
                        }}
                        updating
                      </strong>
                    </p>
                  </c-col>
                  <c-col>
                    <c-button-group class="float-right">
                      <c-button
                        v-if="pluginUpdateCount"
                        size="sm"
                        color="primary"
                        class="update-all-plugins"
                        @click="openConfirmUpdateAllPluginsModal"
                      >
                        <c-spinner v-if="!isSiteUpdateComplete()" color="success" size="sm" />
                        <span v-else class="icon"><i class="fa fa-plug"/></span>
                        <span>Update all</span>
                      </c-button>
                      <c-button
                        v-show="appPluginUpdateAvailable"
                        color="primary"
                        size="sm"
                        @click="updateKoalaBossPlugin"
                      >
                        <c-spinner v-if="isUpdating" color="success" size="sm" />
                        <span v-else class="icon">Update KoalaBoss Plugin</span>
                      </c-button>
                    </c-button-group>
                  </c-col>
                </c-row>
              </template>

              <c-card class="pa-5" v-if="debugMode">
                <c-card-body>
                  <c-row class="">
                    <c-col sm="10">
                      <c-input
                          placeholder="Install plugin: Download url"
                          v-model="packagePath"
                          type="text"
                          autocomplete="off"
                      >
                        <template #prepend-content><c-icon name="cil-plugin" /></template>
                      </c-input>
                    </c-col>
                    <c-col sm="2">
                      <c-button color="primary" @click="installPlugin()">Install</c-button>
                    </c-col>
                  </c-row>
                  <c-row class="" >
                    <c-col sm="12">
                      <c-button-group>
                        <template v-if="isUpdating">
                          <c-button color="primary">Running...</c-button>
                        </template>
                        <template v-else>
                          <c-button color="primary" @click="e => installPluginSet()">Install Set</c-button>
                          <c-button color="primary" @click="togglePluginSetActive('activate')">Activate Set</c-button>
                          <c-button color="primary" @click="togglePluginSetActive('deactivate')">Deactivate Set</c-button>
                        </template>
                      </c-button-group>
                    </c-col>
                  </c-row>
                </c-card-body>
              </c-card>

              <hr />

              <div
                v-for="(plugin, i) in sortPluginsByUpdateRequired(site.scrapeData.pluginData)"
                :key="`${i}_plugin`"
                :class="`plugin-${plugin.active === false ? 'inactive' : 'active'}`"
              >
                <c-row class="px-2 pt-2">
                  <c-col sm="1">
                    <img v-lazy="getPluginThumbUrl(plugin)" width="40px" class="mb-2 mr-3" />
                  </c-col>
                  <c-col>
                    <router-link :class="{'not-active': plugin.active === false}" :to="{ name: 'pluginDetails', params: { pluginKey: getPluginKey(plugin) } }">
                      <span v-html="plugin.Name" />
                    </router-link>
                    <br />
                    <small>{{ plugin.Version }}
                      <span v-if="plugin.key !== appPluginKey" class="mx-1">|</span>
                      <span>
                        <a
                            href="#"
                            v-if="plugin.key !== appPluginKey"
                            size="sm"
                            @click="togglePluginActive(site._id, plugin, plugin.active === true ? 'deactivate' : 'activate')"
                        >
                          {{ plugin.active === true ? 'Deactivate' : 'Activate'}}
                        </a>
                      </span>
                    </small>
                  </c-col>
                  <c-col v-if="plugin.key !== appPluginKey">
                    <template v-if="isPluginBlocked(plugin.key)">
                      <template v-if="plugin.updateFound">
                        <c-button-group class="float-right">
                          <c-button v-show="plugin.updateFound" color="kb-green" class="float-right" size="sm" disabled>
                            Update to {{ getNewPluginVersion(plugin) }}
                          </c-button>
                          <c-button
                            title="Unblock this plugin to allow updating"
                            :disabled="isUpdating"
                            color="plugin-toggle-block"
                            size="sm"
                            @click="togglePluginBlock(site._id, plugin, 'unblock')"
                          >
                            Unblock
                          </c-button>
                        </c-button-group>
                      </template>
                      <template v-else>
                        <c-button
                          color="plugin-toggle-block"
                          title="Unblock this plugin to allow updating"
                          :disabled="isUpdating"
                          size="sm"
                          class="float-right"
                          @click="togglePluginBlock(site._id, plugin, 'unblock')"
                        >
                          Unblock
                        </c-button>
                      </template>
                    </template>
                    <template v-else>
                      <template v-if="plugin.updateFound">
                        <c-button v-if="plugin.isUpdating" class="text-center float-right">
                          <c-spinner color="success" size="sm" />
                        </c-button>
                        <c-dropdown
                          v-else
                          color="kb-green"
                          size="sm"
                          :toggler-text="`Update to ${plugin.updateInfo.newVersion}`"
                          class="float-right plugin-update-dropdown"
                          @click="update(site._id, plugin)"
                        >
                          <c-dropdown-item @click="update(site._id, plugin)">Update</c-dropdown-item>
                          <c-dropdown-item @click="togglePluginBlock(site._id, plugin, 'block')">Block</c-dropdown-item>
                        </c-dropdown>
                      </template>
                      <template v-else>
                        <c-button
                          color="plugin-toggle-block"
                          title="Block this plugin from being updated"
                          class="float-right"
                          :disabled="isUpdating"
                          size="sm"
                          @click="togglePluginBlock(site._id, plugin, 'block')"
                        >
                          Block
                        </c-button>
                      </template>
                    </template>
                  </c-col>
                </c-row>
                <hr class="my-0" />
              </div>
            </template>
            <template v-else>
              <p class="text-h6 is-4">
                There are no site plugins to display, please try
                <a @click="refreshSite">refreshing</a>
                this site.
              </p>
            </template>
          </c-tab>
          <c-tab title="Themes">
            <template #title>
              <theme-icon />
              Themes
              <pill :value="themeUpdateCount" />
            </template>
            <template v-if="parsedSiteData.installedThemes && parsedSiteData.installedThemes.length">
              <table class="table installed-themes">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Version</th>
                    <th>Author</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="(installedTheme, i) in parsedSiteData.installedThemes"
                    :key="`${i}_theme`"
                    :class="{ active: installedTheme.active }"
                  >
                    <td>
                      <router-link
                        :to="{ name: 'themeDetails', params: { themeKey: installedTheme.id } }"
                        v-html="installedTheme.name + (installedTheme.active ? ' (Active)' : '')"
                      />
                    </td>
                    <td>{{ installedTheme.version }}</td>
                    <td v-html="cleanThemeAuthorText(installedTheme.author)" />
                    <td>
                      <div class="control is-grouped is-expanded pull-right manage-buttons">
                        <template v-if="installedTheme.updateFound">
                          <c-button v-if="isUpdating" class="text-center float-right">
                            <c-spinner color="success" size="sm" />
                          </c-button>
                          <c-button
                            v-else
                            color="kb-green"
                            :title="`Update theme to version ${installedTheme.updateInfo.new_version}`"
                            class="btn float-right"
                            :disabled="isUpdating"
                            size="sm"
                            style="min-width: 70px"
                            @click="openConfirmThemeUpdateVersionModal(installedTheme)"
                            v-html="getThemeUpdateButtonText(installedTheme.updateInfo.new_version)"
                          >
                            >
                          </c-button>
                        </template>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </template>
            <template v-else>
              <p>
                No theme information was found. Please update your KoalaBoss plugin to a minimum of version 1.3.4 or
                trying refreshing the site.
              </p>
            </template>
          </c-tab>
          <c-tab v-show="hasTranslationData && site.scrapeData.translationData.length" title="Translations">
            <template #title>
              <c-icon :content="freeSet.cilLanguage" />
              Translations
              <pill v-if="hasTranslationData && site.scrapeData.translationData.length" :value="1" />
            </template>
            <template v-if="!isRefreshingSite && hasTranslationData && site.scrapeData.translationData.length">
              <c-row class="p-1">
                <c-col></c-col>
                <c-col>
                  <c-button
                    class="float-right"
                    color="primary"
                    size="sm"
                    style="min-width: 70px"
                    @click="openConfirmUpdateAllTranslationsModal"
                  >
                    <c-spinner v-if="isUpdatingTranslations" color="black" size="sm" />
                    <span v-else>Update all translations</span>
                  </c-button>
                </c-col>
              </c-row>
              <table class="table translation-list" style="margin-top: 20px">
                <thead>
                  <tr>
                    <th>Type</th>
                    <th>Name</th>
                    <th>Language</th>
                    <th>New Version</th>
                    <th>Date Released</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(translation, i) in site.scrapeData.translationData" :key="`translation_${i}`">
                    <td>
                      {{ translation.type }}
                    </td>
                    <td>
                      {{ translation.slug }}
                    </td>
                    <td>
                      {{ translation.language }}
                    </td>
                    <td>
                      {{ translation.version }}
                    </td>
                    <td>
                      {{ translation.updated | dateFormat }}
                    </td>
                  </tr>
                </tbody>
              </table>
            </template>
          </c-tab>
          <c-tab title="Screenshots">
            <template #title>
              <site-icon />
              Screenshots
            </template>
            <c-row class="p-3">
              <template v-if="!site.screenshots">
                <p class="lead">KoalaBoss is in the process of taking screenshots. Please check again shortly.</p>
              </template>
              <template v-else>
                <c-col>
                  <p class="text-center font-weight-bold">Desktop</p>
                  <p class="text-center">{{ getScreenshotDate('desktop') | dateFormatRelative }}</p>
                  <img
                    v-if="getScreenshotLarge('desktop')"
                    :src="getScreenshotLarge('desktop')"
                    class="screen-shot w-100"
                  />
                </c-col>
                <c-col>
                  <p class="text-center font-weight-bold">Tablet</p>
                  <p class="text-center">{{ getScreenshotDate('tablet') | dateFormatRelative }}</p>
                  <img
                    v-if="getScreenshotLarge('tablet')"
                    :src="getScreenshotLarge('tablet')"
                    class="screen-shot w-100"
                  />
                </c-col>
                <c-col>
                  <p class="text-center font-weight-bold">Mobile</p>
                  <p class="text-center">{{ getScreenshotDate('mobile') | dateFormatRelative }}</p>
                  <img
                    v-if="getScreenshotLarge('mobile')"
                    :src="getScreenshotLarge('mobile')"
                    class="screen-shot w-100"
                  />
                </c-col>
              </template>
            </c-row>
          </c-tab>
          <c-tab title="VRT">
            <template #title>
              <site-icon />
              VRT
            </template>
            <v-r-t :site-id="site._id" />
          </c-tab>
          <c-tab title="Backups">
            <template #title>
              <site-icon />
              Backups
            </template>
            <backups :site-id="site._id" :site-name="site.name" :site="site" />
          </c-tab>
          <c-tab v-if="$store.state.app.authenticatedUser.organisation.name.indexOf('Aspen') !== -1">
            <template #title>
              <c-icon :content="freeSet.cilChartLine" />
              Uptime (beta)
            </template>
            <c-chart-line-example/>
          </c-tab>
        </c-tabs>
        <h2 class="h3 mt-4">Update History</h2>
        <c-button v-if="debugMode" color="primary" @click="relinkLogHistory">Relink Log History</c-button>
        <c-tabs class="mt-4">
          <c-tab title="Plugins">
            <template #title>
              <plugin-icon />
              Plugins
            </template>
            <plugin-update-logs
              :site="site"
              :do-refresh="refreshPluginLogs"
              @rollbackPlugin="rollbackPlugin"
            ></plugin-update-logs>
          </c-tab>
          <c-tab title="WordPress Version">
            <template #title>
              <wordpress-icon/>
              WordPress Version
            </template>
            <core-update-logs :site="site" :do-refresh="refreshCoreUpdateLogs"></core-update-logs>
          </c-tab>
          <c-tab title="Theme Updates">
            <template #title>
              <theme-icon />
              Theme Updates
            </template>
            <theme-update-logs :site="site" :do-refresh="refreshThemeUpdateLogs"></theme-update-logs>
          </c-tab>
        </c-tabs>
      </c-col>
    </c-row>

    <confirm-modal
      title="Update WordPress Version"
      ok-text="Update"
      :show.sync="showWpUpdateVersionModal"
      @update:confirm="updateWpCore"
    >
      <div v-html="wpUpdateVersionMessage" />
    </confirm-modal>

    <confirm-modal
      title="Remove KoalaBoss from site"
      ok-text="Remove"
      :show.sync="showDeleteModal"
      color="danger"
      @update:confirm="deleteSite"
    >
      Remove this site from KoalaBoss? All plugin & theme information, notes and update history will be deleted. Cannot
      undo.
    </confirm-modal>

    <confirm-modal
      title="Update All Translations"
      ok-text="Update all"
      :show.sync="showWpUpdateAllTranslationsModal"
      @update:confirm="updateAllTranslations"
    >
      <div v-html="wpUpdateAllTranslationsMessage" />
    </confirm-modal>

    <confirm-modal title="Update Theme Version" :show.sync="showThemeUpdateVersionModal" @update:confirm="updateTheme">
      <div v-html="themeUpdateVersionMessage" />
    </confirm-modal>

    <confirm-modal
      title="Update All Plugins"
      :show.sync="showWpUpdateAllPluginsModal"
      @update:confirm="updateAllPlugins"
    >
      <div v-html="wpUpdateAllPluginsMessage" />
    </confirm-modal>

    <edit-site :site="site" :show="showEdit" @close="showEdit = false" />
    <edit-site-client :site="site" :show="showEditClient" @close="showEditClient = false" @updated="updateClient" />
  </div>
</template>

<script>
import _ from 'lodash';
import Vue from 'vue';
import VueLazyload from 'vue-lazyload';
import { mapGetters, mapActions } from 'vuex';
import { freeSet } from '@coreui/icons';
import io from 'socket.io-client';
import { iconsSet } from '@/assets/icons/icons';
import Pill from '../components/Pill';
import PluginUpdateLogs from '../components/plugin-update-logs';
import CoreUpdateLogs from '../components/core-update-logs';
import ThemeUpdateLogs from '../components/theme-update-logs';
import ConfirmModal from '@/views/components/ConfirmModal';
import { buildEndpoint } from '@/libs/buildEndPoint';
import { parseScrapeData } from '@/libs/plugin/parseScrapeData';
import stripHtml from '../../libs/sanitization/stripHtml';
import { errorMessage, successMessage } from '@/libs/ui/message';
import SiteIcon from '../components/icons/site';
import PluginIcon from '../components/icons/plugin';
import ThemeIcon from '../components/icons/theme';
import EditSite from '@/views/components/site/edit';
import EditSiteClient from '@/views/components/site/edit-client';
import VRT from '../components/site/vrt';
import InitialScreenshot from '@/views/components/site/initial-screenshot';
import Backups from '@/views/components/site/backups';
import WordpressIcon from '@/views/components/icons/wordpress';
import CChartLineExample from '@/views/charts/CChartLineExample';

const socket = io(buildEndpoint(''));

const placeholderImage = process.env.VUE_APP_PLUGIN_PLACEHOLDER_IMAGE_URL;
Vue.use(VueLazyload, {
  preLoad: 1.3,
  error: placeholderImage,
  loading: placeholderImage,
  attempt: 1,
});

export default {
  name: 'SiteDetails',

  components: {
    Backups,
    Pill,
    PluginUpdateLogs,
    CoreUpdateLogs,
    ThemeUpdateLogs,
    ConfirmModal,
    SiteIcon,
    PluginIcon,
    ThemeIcon,
    WordpressIcon,
    VRT,
    EditSite,
    EditSiteClient,
    InitialScreenshot,
    CChartLineExample,
  },

  data() {
    return {
      iconsSet,
      error: null,
      pluginUndoError: null,
      pluginUpdateError: null,
      paidPluginUpdateError: null,
      translationUpdateError: null,
      pluginUpdateCount: 0,
      translationsUpdateCount: 0,
      showDeleteModal: false,
      showWpUpdateVersionModal: false,
      showWpUpdateAllPluginsModal: false,
      showWpUpdateAllTranslationsModal: false,
      confirmModal: null,
      roleData: null,
      site: null,
      backups: null,
      client: null,
      isUpdating: false,
      isRefreshingSite: false,
      isUpdatingAllPlugins: false,
      isUpdatingTranslations: false,
      isUpdatingTheme: false,
      isSavingNotes: false,
      isEditingNotes: false,
      isTogglingPluginBlock: false,
      siteId: null,
      parsedSiteData: null,
      appPluginUpdateAvailable: false,
      hasPluginData: false,
      hasTranslationData: false,
      screenShotPath: null,
      wpUpdateVersionLocal: null,
      wpUpdateVersionFound: false,
      wpUpdateVersionMessage: '',
      wpUpdateAllPluginsMessage: '',
      wpUpdateAllTranslationsMessage: '',
      wpVersionUpdateError: null,
      refreshPluginLogs: 0,
      refreshCoreUpdateLogs: 0,
      refreshThemeUpdateLogs: 0,
      showThemeUpdateVersionModal: false,
      themeUpdateVersionMessage: '',
      themeVersionUpdateError: null,
      themeToUpdate: {},
      data: {
        body: {
          notes: null,
          notesDisplay: null,
        },
      },
      freeSet,
      showEdit: false,
      showEditClient: false,
      packagePath: '',
    };
  },
  computed: {
    appPluginKey() {
      return process.env.VUE_APP_PLUGIN_KEY;
    },
    themeUpdateCount() {
      const reducer = (accumulator, currentValue) => accumulator + currentValue;
      if (this.parsedSiteData && this.parsedSiteData.installedThemes && this.parsedSiteData.installedThemes.length) {
        const updates = this.parsedSiteData.installedThemes.map((theme) => (theme.updateFound ? 1 : 0));
        return updates.reduce(reducer);
      }
      return 0;
    },
    ...mapGetters({
      debugMode: 'debugMode',
    }),
  },

  created() {
    this.siteId = this.$route.params.siteId;
    const _this = this;
    socket.emit('room', Vue.localStorage.get('mp-sid'));
    socket.on('siteUpdateNotification', (data) => {
      if (['refresh-site'].indexOf(data.action) !== -1 && _this.siteId === data.siteId) {
        _this.isUpdatingAllPlugins = false;
        _this.refreshSite();
        _this.refreshPluginLogs++;
      }
    });
    socket.on('siteThemeUpdateNotification', (data) => {
      if (['refresh-site', 'end-theme-update'].indexOf(data.action) !== -1 && _this.siteId === data.siteId) {
        _this.refreshSite();
        _this.refreshThemeUpdateLogs++;
      }
    });
    this.get({ refresh: true });
  },

  updated() {},

  methods: {
    ...mapActions(['queueSiteUpdate']),
    getPluginThumbUrl(plugin) {
      const pluginKey = plugin.key.substring(0, plugin.key.indexOf('/'));
      return `https://koalaboss-app-plugin-images.s3.ap-southeast-2.amazonaws.com/${pluginKey}.png`;
    },
    getPluginKey(plugin) {
      return plugin.key;
      // return plugin.key.substring(0, plugin.key.indexOf('/'));
    },
    getScreenshotDate(type) {
      return _.get(this.site, `screenshots.${type}.large.createdAt`, '');
    },
    getScreenshotLarge(type) {
      return _.get(this.site, `screenshots.${type}.large.url`, '');
    },
    getScreenshotThumb(type) {
      return _.get(this.site, `screenshots.${type}.thumb.url`, '');
    },
    getSiteFromState() {
      return _.find(this.siteUpdates, { siteId: this.siteId });
    },
    isSiteUpdating() {
      const currentSite = this.getSiteFromState();
      if (!currentSite) {
        return false;
      }
      return currentSite.updateComplete === false;
    },
    isSiteUpdateComplete() {
      const currentSite = this.getSiteFromState();
      if (!currentSite) {
        return true;
      }
      return currentSite.updateComplete === true;
    },
    isPluginBlocked(pluginKey) {
      const blockedPlugins = _.get(this.site, 'updateOpts.blockedPlugins', []);
      return blockedPlugins.indexOf(pluginKey) !== -1;
    },
    getNewPluginVersion(plugin) {
      return _.get(plugin, 'updateInfo.newVersion');
    },
    closeDeleteModal() {
      this.showDeleteModal = false;
    },
    closeUpdateVersionModal() {
      this.showWpUpdateVersionModal = false;
    },
    closeThemeUpdateModal() {
      this.showThemeUpdateVersionModal = false;
    },
    closeUpdateAllPluginsModal() {
      this.showWpUpdateAllPluginsModal = false;
    },
    closeUpdateAllTranslationsModal() {
      this.showWpUpdateAllTranslationsModal = false;
    },
    sortPluginsByUpdateRequired(pluginData) {
      const pluginsRequiringUpdate = _.orderBy(
        pluginData.filter((plugin) => plugin.updateFound === true),
        ['Name', 'asc'],
      );
      const pluginsNotRequiringUpdate = pluginData.filter((plugin) => !plugin.updateFound);
      return pluginsRequiringUpdate.concat(pluginsNotRequiringUpdate);
    },
    deleteSite() {
      this.closeDeleteModal();
      this.$http
        .delete(buildEndpoint('site/admin/delete/') + this.siteId)
        .then((response) => {
          this.error = null;
          if (response.data.success) {
            successMessage(this.$store, { message: 'Site removed from KoalaBoss' });
            this.$router.push({ name: 'dashboard', params: { clientId: this.site.clientId } });
          } else {
            this.error = 'There was a error deleting this site.';
            errorMessage(this.$store, { message: 'There was a error deleting this site.' });
          }
        })
        .catch((error) => {
          this.error = error;
          errorMessage(this.$store, { message: 'There was a error deleting this site.' });
        });
    },
    setModalMessages() {
      this.wpUpdateVersionMessage = `Update this WordPress version from <strong>${
        this.parsedSiteData.wpVersion
      }</strong> to <strong>${
        this.wpUpdateVersionFound
      }</strong>? Cannot rollback.`;
    },
    get(opts) {
      this.$http
        .get(buildEndpoint('site/get/') + this.siteId)
        .then((response) => {
          this.error = null;
          if (response.data.success) {
            this.site = response.data.data.site;
            this.client = response.data.data.client;
            if (_.isString(this.site.notes)) {
              this.data.body.notesDisplay = this.site.notes.replace(/\n/g, '<br/>');
              this.data.body.notes = this.site.notes;
            }
            this.parsedSiteData = parseScrapeData(this.site.scrapeData, this.blockedPlugins);

            if (this.site.scrapeData && this.site.scrapeData.updatedAt) {
              // Override so element shows last scrape date
              this.appPluginUpdateAvailable = this.parsedSiteData.appPluginUpdateAvailable;
              this.site.updatedAt = this.site.scrapeData.updatedAt;
              this.hasPluginData = _.isArray(this.site.scrapeData.pluginData);
              this.hasTranslationData = _.isArray(this.site.scrapeData.translationData);
              this.translationsUpdateCount = this.hasTranslationData ? this.site.scrapeData.translationData.length : 0;
              this.screenShotPath = buildEndpoint(`screenshots/${this.site._id}.png`);
              this.wpUpdateVersionFound = this.parsedSiteData.wpUpdateVersionFound;
              this.wpUpdateVersionLocale = this.parsedSiteData.wpUpdateVersionLocale;
              this.setModalMessages();
              this.setPluginUpdateCount();

              if (opts.refresh) {
                this.refreshSite();
              }
            } else {
              this.refreshSite();
            }
          } else {
            this.error = 'Error getting site data';
          }
        })
        .catch((error) => {
          this.error = error.message;
        });
    },
    openConfirmDelete() {
      this.showDeleteModal = true;
    },
    openConfirmUpdateVersionModal() {
      this.showWpUpdateVersionModal = true;
    },
    openConfirmThemeUpdateVersionModal(theme) {
      this.themeToUpdate = theme;
      this.showThemeUpdateVersionModal = true;
      this.themeUpdateVersionMessage = `Update the <strong>${
        this.themeToUpdate.name
      }</strong> theme version from <strong>${
        this.themeToUpdate.version
      }</strong> to <strong>${
        this.cleanThemeVersionText(_.get(this.themeToUpdate, 'updateInfo.new_version', ''))
      }</strong>? Cannot rollback.`;
    },
    setPluginUpdateCount() {
      this.pluginUpdateCount = 0;
      const blockedPlugins = _.get(this.site, 'updateOpts.blockedPlugins', []);
      // Iterate over all plugins and get a correct update count. Sometimes there is a blocked plugin, which
      // no longer exists on the site, so only check active plugins against the block list.
      this.site.scrapeData.pluginData.forEach((plugin) => {
        if (plugin.updateFound && blockedPlugins.indexOf(plugin.key) === -1) {
          this.pluginUpdateCount++;
        }
      });
    },
    openConfirmUpdateAllPluginsModal() {
      const plural = this.pluginUpdateCount > 1 ? 's' : '';
      this.wpUpdateAllPluginsMessage = `This will update all plugins on <strong>${
        this.site.name
      }</strong>`
        + ' except for any plugins which you have blocked (in this task, '
        + `<strong>${
          this.pluginUpdateCount
        } plugin${
          plural
        }</strong> will be updated).`
        + '<BR><BR>'
        + 'Please check the notification drawer (top right) for realtime progress.';
      this.showWpUpdateAllPluginsModal = true;
    },
    openConfirmUpdateAllTranslationsModal() {
      const plural = this.translationsUpdateCount > 1 ? 's' : '';
      this.wpUpdateAllTranslationsMessage = `This will update all plugin, WordPress version & theme translations on <strong>${
        this.site.name
      }</strong>. `
        + `<BR><BR><strong>${
          this.translationsUpdateCount
        }</strong>`
        + ` translation${
          plural
        } will be updated.`;
      this.showWpUpdateAllTranslationsModal = true;
    },
    updateKoalaBossPlugin() {
      this.isUpdating = true;
      this.pluginUpdateError = null;
      this.$http({
        url: buildEndpoint(`site/plugin/update-koalaboss/${this.siteId}`),
      })
        .then((response) => {
          this.isUpdating = false;
          if (response.data.success) {
            successMessage(this.$store, { message: 'Plugin updated successfully: KoalaBoss' });
            this.refreshSite();
          } else {
            errorMessage(this.$store, { message: response.data.error || response.data.message });
            this.pluginUpdateError = 'Error updating KoalaBoss';
          }
        })
        .catch((error) => {
          this.pluginUpdateError = error;
          errorMessage(this.$store, { message: error.message });
        }).finally(() => {
          this.refreshPluginLogs += 1;
        });
    },
    async installPluginSet() {
      const pluginSet = [
        'https://downloads.wordpress.org/plugin/wp-2fa.2.6.4.zip',
        'https://downloads.wordpress.org/plugin/simple-history.4.15.1.zip',
        'https://downloads.wordpress.org/plugin/wpremote.5.56.zip',
      ];
      this.isUpdating = true;
      try {
        for (const plugin of pluginSet) {
          await this.installPluginAsync(plugin);
        }
        this.refreshSite();
        successMessage(this.$store, { message: 'Set: Installed' });
      } catch (e) {
        errorMessage(this.$store, { message: e.message });
      } finally {
        this.isUpdating = false;
      }
    },
    async installPluginAsync(packagePath = null) {
      await this.$http({
        method: 'put',
        url: buildEndpoint(`plugin/install/${this.siteId}`),
        data: { packagePath: packagePath || this.packagePath },
      });
    },
    async installPlugin(packagePath = null) {
      this.isUpdating = true;
      this.$http({
        method: 'put',
        url: buildEndpoint(`plugin/install/${this.siteId}`),
        data: { packagePath: packagePath || this.packagePath },
      })
        .then((response) => {
          this.isUpdating = false;
          if (response.data.success) {
            successMessage(this.$store, { message: 'Plugin installed successfully' });
            this.refreshSite();
          } else {
            errorMessage(this.$store, { message: response.data.error || response.data.message });
          }
        })
        .catch((error) => {
          this.pluginUpdateError = error;
          errorMessage(this.$store, { message: error.message });
        }).finally(() => {
          this.refreshPluginLogs += 1;
        });
    },
    updateAllPlugins() {
      this.showWpUpdateAllPluginsModal = false;
      this.queueSiteUpdate({
        siteId: this.siteId,
        siteName: this.site.name,
      });
      this.refreshSite();
      this.isUpdatingAllPlugins = true;
      this.pluginUpdateError = null;
      this.$store.commit('openSideBar');
      this.$http
        .get(buildEndpoint(`site/update-all-plugins/${this.siteId}`))
        .then((response) => {
          if (!response.data.success) {
            this.pluginUpdateError = response.data.message + (response.data.error ? `: ${response.data.error}` : '');
          }
        })
        .catch((error) => {
          this.pluginUpdateError = error;
        });
    },
    updateAllTranslations() {
      this.isUpdatingTranslations = true;
      this.showWpUpdateAllTranslationsModal = false;
      this.translationUpdateError = null;
      this.$http
        .post(buildEndpoint(`site/translation/update-all/${this.siteId}`))
        .then((response) => {
          this.isUpdatingTranslations = false;
          if (response.data.success) {
            successMessage(this.$store, { message: 'Translations updated successfully' });
            this.refreshSite();
          } else {
            this.translationUpdateError = response.data.message + (response.data.error ? `: ${response.data.error}` : '');
            errorMessage(this.$store, { message: this.translationUpdateError });
          }
          // this.refreshPluginLogs++
        })
        .catch((error) => {
          this.translationUpdateError = error.message;
          errorMessage(this.$store, { message: this.translationUpdateError });
        });
    },
    updateWpCore() {
      if (!this.wpUpdateVersionFound) {
        return;
      }
      this.isUpdating = true;
      this.showWpUpdateVersionModal = false;
      this.wpVersionUpdateError = null;
      const data = {
        wpVersion: this.wpUpdateVersionFound,
        wpLocale: this.wpUpdateVersionLocale,
      };
      this.$http
        .post(buildEndpoint(`site/core/update/${this.siteId}`), data)
        .then((response) => {
          this.isUpdating = false;
          this.refreshCoreUpdateLogs++;
          if (response.data.success) {
            successMessage(this.$store, {
              message: `WordPress version updated successfully: ${this.wpUpdateVersionFound}`,
            });
            this.refreshSite();
          } else {
            this.wpVersionUpdateError = 'Error updating WordPress version';
            errorMessage(this.$store, { message: this.wpVersionUpdateError });
          }
        })
        .catch((error) => {
          this.wpVersionUpdateError = error.message;
          errorMessage(this.$store, { message: this.wpVersionUpdateError });
        });
    },
    updateTheme() {
      if (!_.has(this.themeToUpdate, 'updateInfo.new_version')) {
        return;
      }
      this.showThemeUpdateVersionModal = false;
      this.themeVersionUpdateError = null;
      const data = {
        siteId: this.siteId,
        themeId: this.themeToUpdate.id,
        themeName: this.themeToUpdate.name,
        currentVersion: this.themeToUpdate.version,
        newVersion: this.themeToUpdate.updateInfo.new_version,
      };
      this.$http
        .post(buildEndpoint('site/theme/update'), data)
        .then((response) => {
          if (response.data.success) {
            this.$store.commit('openSideBar');
          } else {
            this.themeVersionUpdateError = response.data.message + (response.data.error ? `: ${response.data.error}` : '');
          }
        })
        .catch((error) => {
          this.themeVersionUpdateError = error.message;
        });
    },
    async togglePluginSetActive(status) {
      const plugins = [{
        key: 'simple-history/index',
        Name: 'Simple History',
      }, {
        key: 'wp-2fa/wp-2fa',
        Name: 'WP 2FA - Two-factor authentication for WordPress',
      },
      {
        key: 'malcare-security/malcare',
        Name: 'MalCare WordPress Security Plugin - Malware Scanner, Cleaner, Security Firewall',
      },
      ];
      this.isUpdating = true;
      try {
        for (const plugin of plugins) {
          await this.togglePluginActiveAsync(this.siteId, plugin, status);
        }
        this.refreshSite();
        successMessage(this.$store, { message: `Set: ${status}` });
      } catch (e) {
        errorMessage(this.$store, { message: e.message });
      } finally {
        this.isUpdating = false;
      }
    },
    async togglePluginActiveAsync(siteId, plugin, status) {
      await this.$http.put(buildEndpoint(`plugin/${status}/${siteId}`), { pluginPath: plugin.key });
    },
    async togglePluginActive(siteId, plugin, status) {
      this.isUpdating = true;
      plugin.isUpdating = true;
      this.$http
        .put(buildEndpoint(`plugin/${status}/${siteId}`), { pluginPath: plugin.key })
        .then((response) => {
          this.isUpdating = false;
          plugin.isUpdating = false;
          if (response.data.success) {
            successMessage(this.$store, { message: `Plugin ${status}ed successfully: ${plugin.Name}` });
            this.refreshSite();
          } else {
            errorMessage(this.$store, { message: response.data.error || response.data.message });
          }
          this.refreshPluginLogs += 1;
          this.refreshCoreUpdateLogs += 1;
          this.refreshThemeUpdateLogs += 1;
        })
        .catch((error) => {
          errorMessage(this.$store, { message: error });
        }).finally(() => {
          plugin.isUpdating = false;
        });
    },
    relinkLogHistory() {
      this.isUpdating = true;
      this.$http
        .get(buildEndpoint(`site/relink-log-history/${this.siteId}`))
        .then((response) => {
          this.isUpdating = false;
          if (response.data.success) {
            successMessage(this.$store, { message: 'Log history updated' });
          } else {
            errorMessage(this.$store, { message: response.data.error || response.data.message });
          }
          this.refreshPluginLogs++;
        })
        .catch((error) => {
          errorMessage(this.$store, { message: error });
        }).finally(() => {
        // plugin.isUpdating = false;
        });
    },
    update(siteId, plugin) {
      this.isUpdating = true;
      plugin.isUpdating = true;
      this.pluginUpdateError = null;
      this.paidPluginUpdateError = null;
      const pluginData = {
        currentVersion: plugin.Version,
        newVersion: plugin.updateInfo.newVersion,
        name: plugin.Name,
      };
      this.$http
        .post(buildEndpoint(`site/plugin/update/${siteId}/${encodeURIComponent(plugin.key)}`), pluginData)
        .then((response) => {
          this.isUpdating = false;
          plugin.isUpdating = false;
          if (response.data.success) {
            successMessage(this.$store, { message: `Plugin updated successfully: ${plugin.Name}` });
            this.refreshSite();
          } else {
            // const errorProperty = _.get(response.data, 'data.paidPlugin') ? 'paidPluginUpdateError' : 'pluginUpdateError'
            // this[errorProperty] = response.data.error || response.data.message
            errorMessage(this.$store, { message: response.data.error || response.data.message });
          }
          this.refreshPluginLogs++;
        })
        .catch((error) => {
          plugin.isLoading = false;
          plugin.isUpdating = false;
          this.pluginUpdateError = error;
          errorMessage(this.$store, { message: error });
        });
    },
    rollbackPlugin(plugin) {
      this.pluginUndoError = null;
      const targetVersion = plugin.currentVersion;
      const { _id: logId } = plugin;
      const pluginData = {
        logId,
        siteId: this.siteId,
        pluginId: plugin.pluginId,
        targetVersion,
        pluginName: plugin.pluginName,
        currentVersion: 'version currently installed',
      };
      this.isUpdating = true;
      this.$http
        .post(buildEndpoint('site/plugin/restore'), pluginData)
        .then((response) => {
          this.isUpdating = false;
          // Refresh plugin log
          this.refreshPluginLogs++;
          if (response.data.success) {
            successMessage(this.$store, {
              message: `Plugin restored successfully: ${plugin.pluginName} to version ${targetVersion}`,
            });
            this.refreshSite();
          } else {
            this.pluginUndoError = response.data.error || response.data.message;
            errorMessage(this.$store, { message: this.pluginUndoError });
          }
        })
        .catch((error) => {
          this.isUpdating = false;
          // Refresh plugin log
          this.refreshPluginLogs++;
          this.pluginUndoError = error;
          errorMessage(this.$store, { message: this.pluginUndoError });
        });
    },
    togglePluginBlock(siteId, plugin, action) {
      this.isTogglingPluginBlock = true;
      this.pluginUpdateError = null;
      const pluginData = {
        siteId,
        pluginId: plugin.key,
        action,
      };
      this.$http
        .post(buildEndpoint('site/plugin/toggle-block'), pluginData)
        .then((response) => {
          this.isTogglingPluginBlock = false;
          if (response.data.success) {
            this.site.updateOpts.blockedPlugins = response.data.data.blockedPlugins;
            if (plugin.updateFound) {
              if (action === 'block') {
                this.pluginUpdateCount--;
              } else {
                this.pluginUpdateCount++;
              }
            }
          } else {
            this.pluginUpdateError = response.data.message + (response.data.error ? `: ${response.data.error}` : '');
          }
        })
        .catch((error) => {
          this.pluginUpdateError = error;
        });
    },
    refreshSite(opts) {
      this.isRefreshingSite = true;
      this.$http({
        url: buildEndpoint(`site/scrape/${this.siteId}`),
      })
        .then((response) => {
          this.isRefreshingSite = false;
          if (response.data.success) {
            // Get and parse returned data
            this.parsedSiteData = parseScrapeData(response.data.data);
            // Update site name if found - it can change between scrapes
            if (this.parsedSiteData.siteInfo.name !== undefined) {
              this.site.name = this.parsedSiteData.siteInfo.name;
            }
            // Set last updated
            this.site.updatedAt = this.parsedSiteData.updatedAt;
            // Updated plugins
            this.site.scrapeData.pluginData = response.data.data.pluginData;
            this.appPluginUpdateAvailable = this.parsedSiteData.appPluginUpdateAvailable;
            this.hasPluginData = _.isArray(this.site.scrapeData.pluginData);
            // Updated translation
            this.site.scrapeData.translationData = response.data.data.translationData;
            this.hasTranslationData = _.isArray(this.site.scrapeData.translationData);
            this.translationsUpdateCount = this.hasTranslationData ? this.site.scrapeData.translationData.length : 0;
            // Updated screenshot
            this.screenShotPath = buildEndpoint(`screenshots/${this.site._id}.png`);
            // Updated WP version data
            this.wpUpdateVersionFound = this.parsedSiteData.wpUpdateVersionFound;
            this.wpUpdateVersionLocale = this.parsedSiteData.wpUpdateVersionLocale;
            this.setModalMessages();
            this.setPluginUpdateCount();

            if (opts.takeScreenshot) {
              // Trigger screenshot
              this.$http.get(buildEndpoint('site/take-all-screenshots/') + this.siteId).then((response) => {
                // Do nothing
              });
            }

            // Update blocked plugins
            _.set(this.site, 'updateOpts.blockedPlugins', _.get(response, 'data.data.updateOpts.blockedPlugins'));
          } else {
            this.error = response.data.message + (response.data.error ? `: ${response.data.error}` : '');
          }
        })
        .catch((error) => {
          this.isRefreshingSite = false;
          this.error = error;
        });
    },
    editNotes() {
      this.isEditingNotes = true;
    },
    cancelEditNotes() {
      this.isEditingNotes = false;
    },
    saveNotes() {
      this.isSavingNotes = true;
      const data = {
        siteId: this.siteId,
        notes: this.data.body.notes,
      };
      this.$http
        .post(buildEndpoint('site/update-notes/'), data)
        .then((response) => {
          this.isSavingNotes = false;
          if (response.data.success) {
            this.data.body.notesDisplay = this.data.body.notes.replace(/\n/g, '<br/>');
            this.isEditingNotes = false;
          } else {
            this.error = response.data.message + (response.data.error ? `: ${response.data.error}` : '');
          }
        })
        .catch((error) => {
          this.isSavingNotes = false;
          this.error = error;
        });
    },
    cleanThemeVersionText(newVersionText) {
      if (typeof newVersionText !== 'string') {
        return '';
      }
      newVersionText = newVersionText.replace(/<br\/>/g, ' ');
      let firstSpacePosition = newVersionText.indexOf(' ');
      firstSpacePosition = firstSpacePosition === -1 ? newVersionText.length : firstSpacePosition;
      return stripHtml(newVersionText).substr(0, firstSpacePosition);
    },
    cleanThemeAuthorText(themeAuthorText) {
      return stripHtml(themeAuthorText);
    },
    /**
     * Limit what is displayed in the version number, sometimes unexpected text is in there, like:
     * {"new_version" : "6.5.5<br/><a href=\"https://politzdentistry.com/wp-admin/admin.php?page=x-addons-home\">Validate X to enable automatic updates</a>"}
     * @param newVersionText
     * @returns {string}
     */
    getThemeUpdateButtonText(newVersionText) {
      return `Update to ${this.cleanThemeVersionText(newVersionText)}`;
    },
    updateClient(client) {
      this.client = this.client || {};
      this.client._id = client.value;
      this.client.name = client.label;
    },
  },
};
</script>

<style lang="scss" scoped="">
@import '@/assets/scss/colors.scss';
.plugin-list {
  tr {
    &.plugin-blocked {
      /*background-color: lighten(#ffdd57, 20%);*/
      background-color: rgba(217, 171, 72, 0.2);
    }
  }
}
table {
  font-size: 12px;
  thead {
    tr {
      th {
        &.has-text-centered {
          text-align: center !important;
        }
      }
    }
  }

  tbody {
    tr {
      height: 45px;

      &.is-loading {
        background: lighten(#00d1b2, 50%);
      }
    }
  }
}

.notes {
  .read-only {
    height: 75px;
    overflow: auto;
    border: 1px solid silver;
    padding: 5px;
    cursor: pointer;
  }
  .add-notes {
    width: 100%;
    display: block;
    text-align: center;
    text-decoration: underline;
    margin-top: 20px;
  }
}

.installed-themes {
  tbody {
    tr {
      height: 47px;

      &.active {
        td {
          font-weight: bold;
        }
      }
    }
  }
}
.hosting-data {
  span {
    &:first-child {
      width: 150px;
      display: inline-block;
    }
    &:nth-child(2) {
      //font-weight: bold;
    }
  }
}
.not-active {
  color: grey;
}

.plugin-active {
  background: lighten($kb-green, 20);
}
.c-dark-theme {
  .plugin-active {
    background: darken($kb-green, 40);
  }
}
</style>
